import { z } from 'zod';
import { auditFieldsSchema, centsSchema, idSchema, isoDateSchema, paginated, paginationQuerySchema, } from './common.js';
import { measureUnitSchema, positiveQtyMilliSchema, qtyMilliSchema } from './estoque.js';
import { paymentMethodSchema } from './financeiro.js';
/* ============================== Requisições ============================== */
/**
 * O status da requisição vive em DOIS campos, não em um.
 *
 * O enum único anterior misturava as duas perguntas: APROVADA e EM_COTACAO
 * disputavam o mesmo lugar, então assim que a primeira cotação abria a
 * requisição deixava de constar como aprovada — e o comprador não conseguia
 * cotar o resto dos itens. Aprovação é uma pergunta ("passou pelo crivo de
 * quem manda?"), andamento é outra ("quanto disso já foi comprado?").
 */
export const REQUISITION_APPROVALS = [
    'RASCUNHO',
    'AGUARDANDO_APROVACAO',
    'APROVADA',
    'DEVOLVIDA',
    'REPROVADA',
    'CANCELADA',
];
export const requisitionApprovalSchema = z.enum(REQUISITION_APPROVALS);
export const REQUISITION_APPROVAL_LABELS = {
    RASCUNHO: 'Rascunho',
    AGUARDANDO_APROVACAO: 'Aguardando aprovação',
    APROVADA: 'Aprovada',
    DEVOLVIDA: 'Devolvida',
    REPROVADA: 'Reprovada',
    CANCELADA: 'Cancelada',
};
export const REQUISITION_FULFILMENTS = [
    'NAO_INICIADO',
    'EM_COTACAO',
    'PEDIDO_EMITIDO',
    'PARCIAL',
    'ATENDIDA',
];
export const requisitionFulfilmentSchema = z.enum(REQUISITION_FULFILMENTS);
export const REQUISITION_FULFILMENT_LABELS = {
    NAO_INICIADO: 'Não iniciada',
    EM_COTACAO: 'Em cotação',
    PEDIDO_EMITIDO: 'Pedido emitido',
    PARCIAL: 'Parcialmente atendida',
    ATENDIDA: 'Atendida',
};
export const REQUISITION_PRIORITIES = ['NORMAL', 'URGENTE', 'EMERGENCIAL'];
export const requisitionPrioritySchema = z.enum(REQUISITION_PRIORITIES);
export const REQUISITION_PRIORITY_LABELS = {
    NORMAL: 'Normal',
    URGENTE: 'Urgente',
    EMERGENCIAL: 'Emergencial',
};
export const REQUISITION_ITEM_KINDS = ['MATERIAL', 'SERVICO'];
export const requisitionItemKindSchema = z.enum(REQUISITION_ITEM_KINDS);
export const REQUISITION_ITEM_KIND_LABELS = {
    MATERIAL: 'Material',
    SERVICO: 'Serviço',
};
/** Justificativa exigida na emergencial — ela fura a fila de todo mundo. */
export const EMERGENCY_JUSTIFICATION_MIN = 15;
export const requisitionItemSchema = z.object({
    id: idSchema,
    itemId: idSchema.nullable(),
    sku: z.string().nullable(),
    kind: requisitionItemKindSchema,
    description: z.string(),
    unit: measureUnitSchema,
    quantityMilli: z.number().int(),
    estimatedUnitCents: z.number().int(),
    estimatedTotalCents: z.number().int(),
    /// Razão da linha. `pendingMilli` é CALCULADO — nunca gravado —, então não
    /// tem como ficar desencontrado das outras quatro.
    quotingMilli: z.number().int(),
    orderedMilli: z.number().int(),
    attendedMilli: z.number().int(),
    cancelledMilli: z.number().int(),
    pendingMilli: z.number().int(),
    notes: z.string().nullable(),
});
/** Quanto da linha ainda espera Compras. */
export function pendingMilli(item) {
    return Math.max(0, item.quantityMilli -
        item.quotingMilli -
        item.orderedMilli -
        item.attendedMilli -
        item.cancelledMilli);
}
export const requisitionSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    code: z.string(),
    requesterId: idSchema,
    requesterName: z.string().nullable(),
    costCenterId: idSchema,
    costCenterName: z.string().nullable(),
    departmentId: idSchema.nullable(),
    departmentName: z.string().nullable(),
    unitId: idSchema.nullable(),
    approval: requisitionApprovalSchema,
    fulfilment: requisitionFulfilmentSchema,
    priority: requisitionPrioritySchema,
    neededBy: z.string().nullable(),
    justification: z.string().nullable(),
    notes: z.string().nullable(),
    estimatedTotalCents: z.number().int(),
    approvedById: idSchema.nullable(),
    approverName: z.string().nullable(),
    approvedAt: z.string().nullable(),
    /// Teto da alçada que aprovou, em centavos. NULL quando a empresa não tem
    /// faixa configurada. A cotação usa isto para saber se o valor real ainda
    /// cabe no que foi aprovado.
    approvedUpToCents: z.number().int().nullable(),
    rejectedAt: z.string().nullable(),
    rejectionReason: z.string().nullable(),
    returnedAt: z.string().nullable(),
    returnReason: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    items: z.array(requisitionItemSchema),
    /// Soma dos pendentes, para a lista não precisar abrir os itens.
    pendingItemCount: z.number().int(),
});
const requisitionItemInputSchema = z
    .object({
    itemId: idSchema.optional().nullable(),
    kind: requisitionItemKindSchema.default('MATERIAL'),
    description: z.string().trim().min(2, 'Descreva o que é preciso comprar').max(200),
    unit: measureUnitSchema.default('UN'),
    quantityMilli: positiveQtyMilliSchema,
    estimatedUnitCents: centsSchema.min(0).default(0),
    notes: z.string().trim().max(300).optional().nullable(),
})
    .refine((item) => item.kind !== 'SERVICO' || !item.itemId, {
    path: ['itemId'],
    // Serviço não entra no estoque. Se a linha carregasse um item cadastrado,
    // o pedido copiaria esse id e o recebimento daria ENTRADA de material que
    // nunca existiu — saldo inventado a partir de uma mão de obra.
    message: 'Serviço não tem item de estoque. Deixe o item em branco ou troque o tipo para material.',
});
export const requisitionInputSchema = z
    .object({
    costCenterId: idSchema,
    departmentId: idSchema.optional().nullable(),
    unitId: idSchema.optional().nullable(),
    priority: requisitionPrioritySchema.default('NORMAL'),
    neededBy: isoDateSchema.optional().nullable(),
    justification: z.string().trim().max(1000).optional().nullable(),
    notes: z.string().trim().max(1000).optional().nullable(),
    items: z.array(requisitionItemInputSchema).min(1, 'Informe ao menos um item'),
})
    .refine((input) => input.priority !== 'EMERGENCIAL' ||
    (input.justification ?? '').trim().length >= EMERGENCY_JUSTIFICATION_MIN, {
    path: ['justification'],
    message: 'Requisição emergencial exige justificativa: diga o que para se isto não for comprado',
});
export const approveSchema = z.object({
    notes: z.string().trim().max(500).optional().nullable(),
});
export const rejectSchema = z.object({
    reason: z.string().trim().min(5, 'Explique o motivo da reprovação').max(500),
});
export const cancelSchema = z.object({
    reason: z.string().trim().min(5, 'Explique o motivo do cancelamento').max(500),
});
/** Devolver é diferente de reprovar: espera-se a requisição de volta. */
export const returnSchema = z.object({
    reason: z.string().trim().min(5, 'Diga o que o solicitante precisa corrigir').max(500),
});
export const requisitionListQuerySchema = paginationQuerySchema.extend({
    approval: requisitionApprovalSchema.optional(),
    fulfilment: requisitionFulfilmentSchema.optional(),
    priority: requisitionPrioritySchema.optional(),
    costCenterId: idSchema.optional(),
    departmentId: idSchema.optional(),
    unitId: idSchema.optional(),
    requesterId: idSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    /// Só as que ainda têm item esperando Compras — é a fila do comprador.
    pending: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const requisitionListResponseSchema = paginated(requisitionSchema);
/** Contadores do topo da tela. Sempre sobre o filtro em uso, nunca a tabela toda. */
export const requisitionSummarySchema = z.object({
    waitingApproval: z.number().int(),
    approvedPending: z.number().int(),
    inProgress: z.number().int(),
    attended: z.number().int(),
    returnedOrRejected: z.number().int(),
    estimatedPendingCents: z.number().int(),
});
/**
 * Linha do tempo da requisição.
 *
 * Sai da trilha de auditoria que já existe — não há segunda tabela de
 * histórico para manter em dia. O recorte é enxuto de propósito: quem abre a
 * requisição precisa saber quem fez o quê e por quê, não o IP nem o
 * navegador de quem clicou.
 */
export const requisitionTimelineEntrySchema = z.object({
    id: idSchema,
    action: z.string(),
    userName: z.string().nullable(),
    at: z.string(),
    reason: z.string().nullable(),
    changedFields: z.array(z.string()),
    amountCents: z.number().int().nullable(),
});
export const requisitionTimelineResponseSchema = z.array(requisitionTimelineEntrySchema);
/* ---------------------------- faixas de alçada ---------------------------- */
/**
 * Até quanto cada perfil aprova.
 *
 * Antes disso, aprovar era só uma permissão: quem tinha `requisicoes:aprovar`
 * aprovava R$ 200 e R$ 200.000 do mesmo jeito.
 */
export const approvalBandSchema = z.object({
    id: idSchema,
    companyId: idSchema,
    name: z.string(),
    profileId: idSchema,
    profileName: z.string().nullable(),
    /// NULL = sem teto: é a última faixa da escada, a que aprova qualquer valor.
    maxCents: z.number().int().nullable(),
    isActive: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const approvalBandInputSchema = z.object({
    name: z.string().trim().min(2, 'Dê um nome à faixa').max(80),
    profileId: idSchema,
    maxCents: centsSchema.min(0).optional().nullable(),
    isActive: z.boolean().default(true),
});
export const approvalBandListResponseSchema = z.array(approvalBandSchema);
/* ================================ Cotações =============================== */
/**
 * Onde a cotação está no processo.
 *
 * Seis valores, não sete: "aberta" e "aguardando propostas" seriam a mesma
 * coisa com dois nomes, e status sinônimo faz dois compradores marcarem
 * estados diferentes para a mesma situação. A transição acontece pelas AÇÕES
 * do processo — convidar, registrar proposta, escolher, finalizar — nunca por
 * alguém editar o campo na mão.
 */
export const QUOTATION_STATUSES = [
    'RASCUNHO',
    'AGUARDANDO_PROPOSTAS',
    'AGUARDANDO_ANALISE',
    'EM_APROVACAO',
    'FINALIZADA',
    'CANCELADA',
];
export const quotationStatusSchema = z.enum(QUOTATION_STATUSES);
export const QUOTATION_STATUS_LABELS = {
    RASCUNHO: 'Rascunho',
    AGUARDANDO_PROPOSTAS: 'Em andamento',
    AGUARDANDO_ANALISE: 'Aguardando análise',
    EM_APROVACAO: 'Em aprovação',
    FINALIZADA: 'Finalizada',
    CANCELADA: 'Cancelada',
};
export const SUPPLIER_QUOTE_STATUSES = [
    'CONVIDADO',
    'PROPOSTA_RECEBIDA',
    'SEM_RESPOSTA',
    'RECUSOU',
];
export const supplierQuoteStatusSchema = z.enum(SUPPLIER_QUOTE_STATUSES);
export const SUPPLIER_QUOTE_STATUS_LABELS = {
    CONVIDADO: 'Convidado',
    PROPOSTA_RECEBIDA: 'Proposta recebida',
    SEM_RESPOSTA: 'Sem resposta',
    RECUSOU: 'Recusou participar',
};
/** Uma linha da requisição dentro desta cotação. */
export const quotationItemSchema = z.object({
    id: idSchema,
    requisitionItemId: idSchema,
    itemId: idSchema.nullable(),
    sku: z.string().nullable(),
    kind: requisitionItemKindSchema,
    description: z.string(),
    unit: measureUnitSchema,
    /// Quanto desta linha ESTA cotação cota.
    quantityMilli: z.number().int(),
    estimatedUnitCents: z.number().int(),
    estimatedTotalCents: z.number().int(),
    /// Menor preço unitário recebido nesta linha, entre quem respondeu.
    bestUnitPriceCents: z.number().int().nullable(),
    /// Fornecedor que já foi marcado como vencedor desta linha.
    winnerPersonId: idSchema.nullable(),
    winnerSupplierName: z.string().nullable(),
});
export const supplierQuoteItemSchema = z.object({
    id: idSchema,
    quotationItemId: idSchema,
    requisitionItemId: idSchema,
    description: z.string(),
    unit: measureUnitSchema,
    quantityMilli: z.number().int(),
    unitPriceCents: z.number().int(),
    discountCents: z.number().int(),
    totalCents: z.number().int(),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    deliveryDays: z.number().int().nullable(),
    notes: z.string().nullable(),
    /// true quando este fornecedor tem o melhor preço NESTA linha.
    isBestPrice: z.boolean(),
    /// true quando esta linha já foi dada a este fornecedor.
    isWinner: z.boolean(),
});
export const supplierQuoteSchema = z.object({
    id: idSchema,
    quotationId: idSchema,
    personId: idSchema,
    supplierName: z.string(),
    status: supplierQuoteStatusSchema,
    invitedAt: z.string(),
    quotedAt: z.string().nullable(),
    validUntil: z.string().nullable(),
    itemsCents: z.number().int(),
    freightCents: z.number().int(),
    discountCents: z.number().int(),
    totalCents: z.number().int(),
    deliveryDays: z.number().int().nullable(),
    paymentTerms: z.string().nullable(),
    contactName: z.string().nullable(),
    notes: z.string().nullable(),
    declineReason: z.string().nullable(),
    /// true quando é a proposta de MENOR total ENTRE AS COMPLETAS — proposta
    /// parcial (que não cotou todos os itens) nunca "vence" o comparativo.
    isLowestTotal: z.boolean(),
    /// false quando o fornecedor cotou só parte dos itens da cotação.
    coversAllItems: z.boolean(),
    /// Quantas linhas este fornecedor levou.
    wonItemCount: z.number().int(),
    /// Soma do que ele levou — é o que o pedido dele vai valer.
    wonTotalCents: z.number().int(),
    items: z.array(supplierQuoteItemSchema),
});
export const quotationSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    code: z.string(),
    requisitionId: idSchema,
    requisitionCode: z.string().nullable(),
    costCenterName: z.string().nullable(),
    departmentName: z.string().nullable(),
    requisitionPriority: requisitionPrioritySchema,
    status: quotationStatusSchema,
    dueDate: z.string().nullable(),
    notes: z.string().nullable(),
    winnerQuoteId: idSchema.nullable(),
    decisionReason: z.string().nullable(),
    decidedAt: z.string().nullable(),
    finalizedAt: z.string().nullable(),
    approvalRequestedAt: z.string().nullable(),
    approvedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    items: z.array(quotationItemSchema),
    supplierQuotes: z.array(supplierQuoteSchema),
    /// Contadores para a lista não precisar abrir as propostas.
    invitedCount: z.number().int(),
    respondedCount: z.number().int(),
    /// Total do que foi escolhido, somando os vencedores de cada linha.
    awardedTotalCents: z.number().int(),
    /// Estimado da requisição para as linhas desta cotação.
    estimatedTotalCents: z.number().int(),
    /// Estimado menos escolhido. NEGATIVO quando a compra saiu mais cara do que
    /// a obra imaginou — e nesse caso é isso mesmo que tem de aparecer.
    savingsCents: z.number().int(),
    /// Teto da alçada que aprovou a requisição. Null quando não há faixa.
    approvedUpToCents: z.number().int().nullable(),
    /// true quando o valor escolhido passa desse teto: vai para reaprovação.
    needsReapproval: z.boolean(),
});
/**
 * Abertura da cotação.
 *
 * Os itens são ESCOLHIDOS, com quantidade própria. Uma requisição pode virar
 * duas cotações (os elétricos numa, os de automação noutra), e cotar 60 dos
 * 100 cabos é caso normal.
 */
export const quotationInputSchema = z.object({
    requisitionId: idSchema,
    dueDate: isoDateSchema.optional().nullable(),
    notes: z.string().trim().max(1000).optional().nullable(),
    items: z
        .array(z.object({
        requisitionItemId: idSchema,
        quantityMilli: positiveQtyMilliSchema,
    }))
        .min(1, 'Escolha ao menos um item para cotar'),
    /// Fornecedores convidados já na abertura. Podem ser adicionados depois.
    personIds: z.array(idSchema).default([]),
});
export const quotationUpdateSchema = z.object({
    dueDate: isoDateSchema.optional().nullable(),
    notes: z.string().trim().max(1000).optional().nullable(),
});
export const inviteSupplierSchema = z.object({
    personId: idSchema,
    contactName: z.string().trim().max(120).optional().nullable(),
});
/** Fornecedor que não respondeu ou recusou — sem inventar proposta. */
export const supplierAnswerSchema = z.object({
    status: z.enum(['SEM_RESPOSTA', 'RECUSOU']),
    reason: z.string().trim().max(500).optional().nullable(),
});
export const supplierQuoteInputSchema = z.object({
    personId: idSchema,
    quotedAt: isoDateSchema.optional().nullable(),
    validUntil: isoDateSchema.optional().nullable(),
    deliveryDays: z.number().int().min(0).max(365).optional().nullable(),
    paymentTerms: z.string().trim().max(120).optional().nullable(),
    contactName: z.string().trim().max(120).optional().nullable(),
    freightCents: centsSchema.min(0).default(0),
    discountCents: centsSchema.min(0).default(0),
    notes: z.string().trim().max(500).optional().nullable(),
    items: z
        .array(z.object({
        quotationItemId: idSchema,
        /// Quanto o fornecedor se dispôs a entregar desta linha.
        quantityMilli: positiveQtyMilliSchema,
        unitPriceCents: centsSchema.min(0),
        discountCents: centsSchema.min(0).default(0),
        brand: z.string().trim().max(80).optional().nullable(),
        model: z.string().trim().max(80).optional().nullable(),
        deliveryDays: z.number().int().min(0).max(365).optional().nullable(),
        notes: z.string().trim().max(300).optional().nullable(),
    }))
        .min(1, 'Informe o preço de ao menos um item'),
});
/**
 * Escolha dos vencedores, LINHA A LINHA.
 *
 * A justificativa é obrigatória quando alguma linha escolhida não é a de
 * menor preço — é a pergunta que a auditoria faria seis meses depois,
 * respondida na hora da decisão.
 */
export const awardQuotationSchema = z.object({
    awards: z
        .array(z.object({
        quotationItemId: idSchema,
        supplierQuoteId: idSchema,
    }))
        .min(1, 'Escolha o vencedor de ao menos um item'),
    reason: z.string().trim().max(1000).optional().nullable(),
});
export const quotationListQuerySchema = paginationQuerySchema.extend({
    status: quotationStatusSchema.optional(),
    requisitionId: idSchema.optional(),
    personId: idSchema.optional(),
    costCenterId: idSchema.optional(),
    departmentId: idSchema.optional(),
    priority: requisitionPrioritySchema.optional(),
    buyerId: idSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    minCents: centsSchema.min(0).optional(),
    maxCents: centsSchema.min(0).optional(),
    /// Só as que já têm vencedor definido.
    awarded: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const quotationListResponseSchema = paginated(quotationSchema);
/**
 * Contadores do topo da tela.
 *
 * `estimatedSavingsCents` soma o estimado menos o escolhido, SÓ das cotações
 * finalizadas. É a única base honesta que o banco tem: a requisição já traz
 * um preço estimado por item, e comparar o que se pagou com o que se
 * imaginava pagar é economia de verdade. A diferença entre a maior e a menor
 * proposta, que a tela mostrava antes, é dispersão entre fornecedores — não
 * dinheiro que deixou de sair.
 */
export const quotationSummarySchema = z.object({
    inProgress: z.number().int(),
    waitingAnalysis: z.number().int(),
    inApproval: z.number().int(),
    finished: z.number().int(),
    cancelled: z.number().int(),
    estimatedSavingsCents: z.number().int(),
});
export const quotationTimelineResponseSchema = requisitionTimelineResponseSchema;
/* ============================ Pedidos de compra ========================== */
/**
 * O pedido também vive em DOIS campos.
 *
 * O enum único misturava o ciclo do pedido com o andamento da entrega:
 * `ENVIADO`/`CONFIRMADO` falavam do pedido, `PARCIALMENTE_RECEBIDO`/`RECEBIDO`
 * falavam do material. Assim que a primeira caixa chegava, o pedido deixava de
 * constar como emitido — e não havia onde dizer "aguardando aprovação". É o
 * mesmo remédio da requisição.
 */
export const PURCHASE_ORDER_STATUSES = [
    'RASCUNHO',
    'EM_APROVACAO',
    'APROVADO',
    'EMITIDO',
    'CANCELADO',
    'ENCERRADO',
];
export const purchaseOrderStatusSchema = z.enum(PURCHASE_ORDER_STATUSES);
export const PURCHASE_ORDER_STATUS_LABELS = {
    RASCUNHO: 'Rascunho',
    EM_APROVACAO: 'Em aprovação',
    APROVADO: 'Aprovado',
    EMITIDO: 'Em aberto',
    CANCELADO: 'Cancelado',
    ENCERRADO: 'Entregue',
};
/** CALCULADO a partir do saldo das linhas. Nunca digitado na tela do pedido. */
export const PURCHASE_ORDER_RECEIVINGS = ['NAO_RECEBIDO', 'PARCIAL', 'RECEBIDO'];
export const purchaseOrderReceivingSchema = z.enum(PURCHASE_ORDER_RECEIVINGS);
export const PURCHASE_ORDER_RECEIVING_LABELS = {
    NAO_RECEBIDO: 'Não recebido',
    PARCIAL: 'Parcial',
    RECEBIDO: 'Recebido',
};
export const PURCHASE_ORDER_ORIGINS = ['COTACAO', 'AVULSO'];
export const purchaseOrderOriginSchema = z.enum(PURCHASE_ORDER_ORIGINS);
export const PURCHASE_ORDER_ORIGIN_LABELS = {
    COTACAO: 'De cotação',
    AVULSO: 'Avulso',
};
/**
 * Por que a compra saiu do fluxo.
 *
 * O avulso existe porque a compra de emergência existe — mas fica MARCADO,
 * com motivo e justificativa, para não virar a porta dos fundos de
 * requisição → cotação → aprovação.
 */
export const PURCHASE_ORDER_AVULSO_REASONS = [
    'EMERGENCIAL',
    'BALCAO',
    'FORNECEDOR_EXCLUSIVO',
    'SERVICO_URGENTE',
    'OUTRO',
];
export const purchaseOrderAvulsoReasonSchema = z.enum(PURCHASE_ORDER_AVULSO_REASONS);
export const PURCHASE_ORDER_AVULSO_REASON_LABELS = {
    EMERGENCIAL: 'Compra emergencial',
    BALCAO: 'Compra de balcão',
    FORNECEDOR_EXCLUSIVO: 'Fornecedor exclusivo',
    SERVICO_URGENTE: 'Serviço urgente',
    OUTRO: 'Outro',
};
export const FREIGHT_MODES = ['INCLUSO', 'FORNECEDOR', 'COMPRADOR'];
export const freightModeSchema = z.enum(FREIGHT_MODES);
export const FREIGHT_MODE_LABELS = {
    INCLUSO: 'Incluso no preço',
    FORNECEDOR: 'Por conta do fornecedor',
    COMPRADOR: 'Por conta do comprador (CIF)',
};
export const purchaseOrderItemSchema = z.object({
    id: idSchema,
    kind: requisitionItemKindSchema,
    itemId: idSchema.nullable(),
    sku: z.string().nullable(),
    /// De qual linha da requisição e de qual linha da cotação esta veio. É a
    /// rastreabilidade que o recebimento e a auditoria seguem depois.
    requisitionItemId: idSchema.nullable(),
    quotationItemId: idSchema.nullable(),
    supplierQuoteItemId: idSchema.nullable(),
    description: z.string(),
    specification: z.string().nullable(),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    unit: measureUnitSchema,
    quantityMilli: z.number().int(),
    receivedQuantityMilli: z.number().int(),
    cancelledQuantityMilli: z.number().int(),
    /// pedido − recebido − cancelado. Não é coluna: sai da subtração, e por isso
    /// não tem como ficar desencontrado do resto.
    pendingQuantityMilli: z.number().int(),
    unitPriceCents: z.number().int(),
    discountCents: z.number().int(),
    totalCents: z.number().int(),
});
/** Uma alteração do pedido já emitido: o que era, o que virou, quem e por quê. */
export const purchaseOrderRevisionSchema = z.object({
    id: idSchema,
    number: z.number().int(),
    reason: z.string(),
    totalBeforeCents: z.number().int(),
    totalAfterCents: z.number().int(),
    reapproved: z.boolean(),
    changedById: idSchema.nullable(),
    changedByName: z.string().nullable(),
    createdAt: z.string(),
});
export const purchaseOrderSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    code: z.string(),
    origin: purchaseOrderOriginSchema,
    avulsoReason: purchaseOrderAvulsoReasonSchema.nullable(),
    avulsoJustification: z.string().nullable(),
    requisitionId: idSchema.nullable(),
    requisitionCode: z.string().nullable(),
    quotationId: idSchema.nullable(),
    quotationCode: z.string().nullable(),
    personId: idSchema,
    supplierName: z.string(),
    /// Razão social e contato do fornecedor. Vão junto porque o pedido é
    /// IMPRESSO e entregue: a folha precisa identificar quem vende sem obrigar
    /// quem compra a ter permissão de abrir o cadastro de pessoas.
    supplierLegalName: z.string(),
    supplierDocument: z.string().nullable(),
    supplierPhone: z.string().nullable(),
    supplierEmail: z.string().nullable(),
    costCenterId: idSchema,
    costCenterName: z.string().nullable(),
    unitId: idSchema.nullable(),
    unitName: z.string().nullable(),
    locationId: idSchema.nullable(),
    locationName: z.string().nullable(),
    deliveryAddress: z.string().nullable(),
    buyerId: idSchema.nullable(),
    buyerName: z.string().nullable(),
    status: purchaseOrderStatusSchema,
    receiving: purchaseOrderReceivingSchema,
    /// Derivado, não gravado: tem prazo vencido e ainda falta chegar coisa.
    overdue: z.boolean(),
    /// Soma dos itens, sem frete e sem desconto.
    itemsCents: z.number().int(),
    freightCents: z.number().int(),
    freightMode: freightModeSchema.nullable(),
    carrierName: z.string().nullable(),
    freightNotes: z.string().nullable(),
    discountCents: z.number().int(),
    /// itens + frete − desconto: o que será pago de fato.
    totalCents: z.number().int(),
    deliveryDays: z.number().int().nullable(),
    paymentTerms: z.string().nullable(),
    paymentMethod: paymentMethodSchema.nullable(),
    expectedAt: z.string().nullable(),
    supplierInstructions: z.string().nullable(),
    approvedById: idSchema.nullable(),
    approvedAt: z.string().nullable(),
    approvedUpToCents: z.number().int().nullable(),
    returnedAt: z.string().nullable(),
    returnReason: z.string().nullable(),
    issuedAt: z.string().nullable(),
    sentAt: z.string().nullable(),
    confirmedAt: z.string().nullable(),
    receivedAt: z.string().nullable(),
    closedAt: z.string().nullable(),
    closeReason: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    revision: z.number().int(),
    notes: z.string().nullable(),
    items: z.array(purchaseOrderItemSchema),
    revisions: z.array(purchaseOrderRevisionSchema),
    /// Lançamento financeiro gerado a partir deste pedido, quando houver.
    financialEntryId: idSchema.nullable(),
});
const orderItemInputSchema = z.object({
    itemId: idSchema.optional().nullable(),
    /// MATERIAL entra no estoque ao chegar; SERVIÇO é aceito, não contado.
    kind: requisitionItemKindSchema.default('MATERIAL'),
    description: z.string().trim().min(2, 'Descreva o item').max(200),
    specification: z.string().trim().max(500).optional().nullable(),
    brand: z.string().trim().max(80).optional().nullable(),
    model: z.string().trim().max(80).optional().nullable(),
    unit: measureUnitSchema.default('UN'),
    quantityMilli: positiveQtyMilliSchema,
    unitPriceCents: centsSchema.min(0),
    discountCents: centsSchema.min(0).default(0),
});
/** O que é comum aos dois jeitos de montar um pedido. */
const orderCommercialSchema = z.object({
    costCenterId: idSchema,
    unitId: idSchema.optional().nullable(),
    locationId: idSchema.optional().nullable(),
    deliveryAddress: z.string().trim().max(300).optional().nullable(),
    deliveryDays: z.number().int().min(0).max(365).optional().nullable(),
    paymentTerms: z.string().trim().max(120).optional().nullable(),
    paymentMethod: paymentMethodSchema.optional().nullable(),
    /// Frete cobrado pelo fornecedor. Entra no custo de entrada do estoque
    /// rateado pelo valor de cada item — senão o custo médio nasce menor.
    freightCents: centsSchema.min(0).default(0),
    freightMode: freightModeSchema.optional().nullable(),
    carrierName: z.string().trim().max(120).optional().nullable(),
    freightNotes: z.string().trim().max(300).optional().nullable(),
    discountCents: centsSchema.min(0).default(0),
    expectedAt: isoDateSchema.optional().nullable(),
    supplierInstructions: z.string().trim().max(1000).optional().nullable(),
    notes: z.string().trim().max(1000).optional().nullable(),
});
/**
 * Pedido AVULSO: o que não passou por cotação.
 *
 * Motivo e justificativa são obrigatórios — é o que separa "compra de
 * emergência registrada" de "alguém pulou o processo". Em OUTRO a
 * justificativa precisa dizer alguma coisa, não só existir.
 */
export const purchaseOrderInputSchema = orderCommercialSchema
    .extend({
    personId: idSchema,
    requisitionId: idSchema.optional().nullable(),
    avulsoReason: purchaseOrderAvulsoReasonSchema,
    avulsoJustification: z.string().trim().max(1000).optional().nullable(),
    items: z.array(orderItemInputSchema).min(1, 'Informe ao menos um item'),
})
    .refine((v) => v.avulsoReason !== 'OUTRO' || (v.avulsoJustification ?? '').trim().length >= 10, {
    path: ['avulsoJustification'],
    message: 'Em "Outro", explique o motivo da compra avulsa (mínimo 10 caracteres)',
});
/**
 * Pedido a partir da cotação decidida — o caminho normal.
 *
 * `personId` diz para QUAL fornecedor emitir. Cotação com vencedor por item
 * gera um pedido por fornecedor: sem isto, não havia como emitir nenhum dos
 * dois, e a tela mandava o comprador para uma função que não existia.
 */
export const orderFromQuotationSchema = orderCommercialSchema.partial({ costCenterId: true }).extend({
    quotationId: idSchema,
    personId: idSchema,
});
/** Alteração de pedido JÁ EMITIDO: guarda o antes, exige motivo. */
export const purchaseOrderReviseSchema = orderCommercialSchema.extend({
    reason: z.string().trim().min(10, 'Explique o motivo da alteração').max(500),
    items: z.array(orderItemInputSchema).min(1, 'Informe ao menos um item'),
});
/** Encerrar o saldo: o que não vem mais deixa de aparecer como pendente. */
export const purchaseOrderCloseSchema = z.object({
    reason: z.string().trim().min(5, 'Diga por que o saldo está sendo encerrado').max(500),
});
export const purchaseOrderListQuerySchema = paginationQuerySchema.extend({
    status: purchaseOrderStatusSchema.optional(),
    receiving: purchaseOrderReceivingSchema.optional(),
    origin: purchaseOrderOriginSchema.optional(),
    personId: idSchema.optional(),
    costCenterId: idSchema.optional(),
    unitId: idSchema.optional(),
    locationId: idSchema.optional(),
    buyerId: idSchema.optional(),
    requisitionId: idSchema.optional(),
    quotationId: idSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    minCents: centsSchema.min(0).optional(),
    maxCents: centsSchema.min(0).optional(),
    /// Prazo vencido com item ainda pendente.
    overdue: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const purchaseOrderListResponseSchema = paginated(purchaseOrderSchema);
/** Contadores do topo da tela. Sempre sobre o filtro em uso, nunca a tabela toda. */
export const purchaseOrderSummarySchema = z.object({
    open: z.number().int(),
    delivered: z.number().int(),
    partial: z.number().int(),
    cancelled: z.number().int(),
    totalCents: z.number().int(),
});
/**
 * O que uma cotação finalizada ainda tem para virar pedido.
 *
 * Um bloco por fornecedor vencedor, já com o que ele levou e o que já foi
 * encaminhado — é o que a tela usa para oferecer "emitir pedido" por
 * fornecedor sem deixar ninguém pedir duas vezes a mesma quantidade.
 */
export const quotationPendingSupplierSchema = z.object({
    personId: idSchema,
    supplierName: z.string(),
    supplierDocument: z.string().nullable(),
    supplierQuoteId: idSchema,
    itemCount: z.number().int(),
    totalCents: z.number().int(),
    /// Já existe pedido vivo cobrindo tudo o que este fornecedor venceu.
    alreadyOrdered: z.boolean(),
    orderCodes: z.array(z.string()),
});
export const quotationPendingOrdersSchema = z.object({
    quotationId: idSchema,
    quotationCode: z.string(),
    requisitionId: idSchema,
    requisitionCode: z.string(),
    costCenterId: idSchema,
    suppliers: z.array(quotationPendingSupplierSchema),
});
/** Histórico do pedido. Mesmo formato do da requisição — sai da auditoria. */
export const purchaseOrderTimelineResponseSchema = requisitionTimelineResponseSchema;
/* ============================== Recebimento ============================== */
/**
 * Ciclo OPERACIONAL do recebimento. Divergência e estoque são perguntas
 * separadas (`hasDivergence`, `stockStatus`): um status só que tentasse
 * dizer tudo viraria CONCLUIDO_COM_DIVERGENCIA_PARCIAL.
 */
export const RECEIVING_STATUSES = ['RASCUNHO', 'EM_CONFERENCIA', 'CONCLUIDO', 'CANCELADO'];
export const receivingStatusSchema = z.enum(RECEIVING_STATUSES);
export const RECEIVING_STATUS_LABELS = {
    RASCUNHO: 'Rascunho',
    EM_CONFERENCIA: 'Em conferência',
    CONCLUIDO: 'Concluído',
    CANCELADO: 'Cancelado',
};
/** O que aconteceu com o estoque por causa deste recebimento. */
export const RECEIVING_STOCK_STATUSES = ['PENDENTE', 'INTEGRADO', 'NAO_APLICAVEL', 'ESTORNADO'];
export const receivingStockStatusSchema = z.enum(RECEIVING_STOCK_STATUSES);
export const RECEIVING_STOCK_STATUS_LABELS = {
    PENDENTE: 'Pendente',
    INTEGRADO: 'Integrado',
    NAO_APLICAVEL: 'Não se aplica',
    ESTORNADO: 'Estornado',
};
/**
 * Situação EXIBIDA — derivada, nunca gravada. É o que a tabela mostra numa
 * coluna só: rascunho e conferência aberta são "em conferência"; concluído
 * com divergência em aberto é "com divergência"; concluído deixando saldo
 * é "parcial"; o resto é "recebido".
 */
export const RECEIVING_SITUATIONS = [
    'EM_CONFERENCIA',
    'RECEBIDO',
    'PARCIAL',
    'COM_DIVERGENCIA',
    'CANCELADO',
];
export const receivingSituationSchema = z.enum(RECEIVING_SITUATIONS);
export const RECEIVING_SITUATION_LABELS = {
    EM_CONFERENCIA: 'Em conferência',
    RECEBIDO: 'Recebido',
    PARCIAL: 'Parcial',
    COM_DIVERGENCIA: 'Com divergência',
    CANCELADO: 'Cancelado',
};
export const RECEIVING_ITEM_CONDITIONS = [
    'OK',
    'FALTA',
    'EXCESSO',
    'AVARIADO',
    'ITEM_INCORRETO',
    'ESPECIFICACAO_DIVERGENTE',
    'RECUSADO',
];
export const receivingItemConditionSchema = z.enum(RECEIVING_ITEM_CONDITIONS);
export const RECEIVING_ITEM_CONDITION_LABELS = {
    OK: 'OK',
    FALTA: 'Falta',
    EXCESSO: 'Excesso',
    AVARIADO: 'Avariado',
    ITEM_INCORRETO: 'Item incorreto',
    ESPECIFICACAO_DIVERGENTE: 'Especificação divergente',
    RECUSADO: 'Recusado',
};
/** Aceite de SERVIÇO: não há caixa para contar, há um trabalho para aprovar. */
export const SERVICE_ACCEPTANCES = ['CONCLUIDO', 'PARCIAL', 'RECUSADO', 'AGUARDANDO_CORRECAO'];
export const serviceAcceptanceSchema = z.enum(SERVICE_ACCEPTANCES);
export const SERVICE_ACCEPTANCE_LABELS = {
    CONCLUIDO: 'Concluído',
    PARCIAL: 'Parcialmente concluído',
    RECUSADO: 'Recusado',
    AGUARDANDO_CORRECAO: 'Aguardando correção',
};
export const RECEIVING_DIVERGENCE_TYPES = [
    'FALTA',
    'EXCESSO',
    'AVARIA',
    'ITEM_INCORRETO',
    'ESPECIFICACAO_DIVERGENTE',
    'MARCA_DIVERGENTE',
    'MODELO_DIVERGENTE',
    'EMBALAGEM_VIOLADA',
    'QUANTIDADE_INCORRETA',
    'OUTRO',
];
export const receivingDivergenceTypeSchema = z.enum(RECEIVING_DIVERGENCE_TYPES);
export const RECEIVING_DIVERGENCE_TYPE_LABELS = {
    FALTA: 'Falta',
    EXCESSO: 'Excesso',
    AVARIA: 'Avaria',
    ITEM_INCORRETO: 'Item incorreto',
    ESPECIFICACAO_DIVERGENTE: 'Especificação divergente',
    MARCA_DIVERGENTE: 'Marca divergente',
    MODELO_DIVERGENTE: 'Modelo divergente',
    EMBALAGEM_VIOLADA: 'Embalagem violada',
    QUANTIDADE_INCORRETA: 'Quantidade incorreta',
    OUTRO: 'Outro',
};
export const RECEIVING_DIVERGENCE_STATUSES = ['ABERTA', 'EM_ANALISE', 'RESOLVIDA', 'ACEITA', 'RECUSADA'];
export const receivingDivergenceStatusSchema = z.enum(RECEIVING_DIVERGENCE_STATUSES);
export const RECEIVING_DIVERGENCE_STATUS_LABELS = {
    ABERTA: 'Aberta',
    EM_ANALISE: 'Em análise',
    RESOLVIDA: 'Resolvida',
    ACEITA: 'Aceita',
    RECUSADA: 'Recusada',
};
/** Divergência que ainda pede alguém: é o que conta como "com divergência". */
export const OPEN_DIVERGENCE_STATUSES = ['ABERTA', 'EM_ANALISE'];
export const receivingItemSchema = z.object({
    id: idSchema,
    purchaseOrderItemId: idSchema,
    itemId: idSchema.nullable(),
    sku: z.string().nullable(),
    kind: requisitionItemKindSchema,
    description: z.string(),
    specification: z.string().nullable(),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    unit: measureUnitSchema,
    /// Quanto o pedido pediu desta linha, no total.
    orderedQuantityMilli: z.number().int(),
    /// Quanto já tinha chegido em recebimentos ANTERIORES a este.
    previouslyReceivedMilli: z.number().int(),
    /// Saldo do pedido quando esta conferência foi aberta.
    expectedQuantityMilli: z.number().int(),
    deliveredQuantityMilli: z.number().int(),
    acceptedQuantityMilli: z.number().int(),
    rejectedQuantityMilli: z.number().int(),
    /// O que continua faltando do pedido depois desta conferência.
    pendingQuantityMilli: z.number().int(),
    condition: receivingItemConditionSchema,
    serviceStatus: serviceAcceptanceSchema.nullable(),
    notes: z.string().nullable(),
    unitCostCents: z.number().int(),
    /// Aceita × custo — o que de fato entrou.
    totalCents: z.number().int(),
    /// Linha de MATERIAL amarrada a um item cadastrado: só ela movimenta estoque.
    controlsStock: z.boolean(),
    /// Item que exige lote e validade na conferência.
    tracksBatch: z.boolean(),
    batchCode: z.string().nullable(),
    batchExpiry: z.string().nullable(),
    stockMovementId: idSchema.nullable(),
});
export const receivingDivergenceSchema = z.object({
    id: idSchema,
    receivingItemId: idSchema.nullable(),
    itemDescription: z.string().nullable(),
    type: receivingDivergenceTypeSchema,
    status: receivingDivergenceStatusSchema,
    quantityMilli: z.number().int(),
    description: z.string(),
    resolution: z.string().nullable(),
    resolvedById: idSchema.nullable(),
    resolvedByName: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    createdAt: z.string(),
    createdById: idSchema.nullable(),
    createdByName: z.string().nullable(),
});
export const receivingSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    code: z.string(),
    purchaseOrderId: idSchema,
    purchaseOrderCode: z.string().nullable(),
    requisitionId: idSchema.nullable(),
    requisitionCode: z.string().nullable(),
    quotationId: idSchema.nullable(),
    quotationCode: z.string().nullable(),
    personId: idSchema.nullable(),
    supplierName: z.string().nullable(),
    supplierDocument: z.string().nullable(),
    costCenterId: idSchema.nullable(),
    costCenterName: z.string().nullable(),
    unitId: idSchema.nullable(),
    unitName: z.string().nullable(),
    locationId: idSchema,
    locationName: z.string().nullable(),
    status: receivingStatusSchema,
    situation: receivingSituationSchema,
    stockStatus: receivingStockStatusSchema,
    receivedAt: z.string(),
    receivedById: idSchema.nullable(),
    receivedByName: z.string().nullable(),
    hasDivergence: z.boolean(),
    documentNumber: z.string().nullable(),
    documentKey: z.string().nullable(),
    fiscalDocumentId: idSchema.nullable(),
    notes: z.string().nullable(),
    concludedAt: z.string().nullable(),
    concludedById: idSchema.nullable(),
    concludedByName: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    itemCount: z.number().int(),
    /// Soma do ACEITO × custo: o valor que entrou.
    totalCents: z.number().int(),
    items: z.array(receivingItemSchema),
    divergences: z.array(receivingDivergenceSchema),
});
/** Chave de acesso da NF-e: 44 dígitos, com ou sem espaços/pontos no meio. */
export const accessKeySchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => v === '' || v.length === 44, 'A chave de acesso tem 44 dígitos');
/**
 * Uma linha da conferência.
 *
 * ENTREGUE é o que o fornecedor trouxe; ACEITA é o que passou; RECUSADA é a
 * diferença. Quando só a entregue vem, aceita = entregue e recusada = 0.
 * Aceitar mais do que o saldo é EXCESSO: só com permissão própria e com a
 * justificativa — o serviço confere as duas coisas.
 */
export const receivingLineInputSchema = z
    .object({
    purchaseOrderItemId: idSchema,
    deliveredQuantityMilli: qtyMilliSchema.min(0),
    acceptedQuantityMilli: qtyMilliSchema.min(0).optional(),
    rejectedQuantityMilli: qtyMilliSchema.min(0).optional(),
    condition: receivingItemConditionSchema.optional(),
    serviceStatus: serviceAcceptanceSchema.optional().nullable(),
    notes: z.string().trim().max(500).optional().nullable(),
    unitCostCents: centsSchema.min(0).optional(),
    excessJustification: z.string().trim().max(500).optional().nullable(),
    /// Lote e validade da embalagem que chegou, quando o item controla lote.
    batchCode: z.string().trim().max(60).toUpperCase().optional().nullable(),
    batchExpiry: isoDateSchema.optional().nullable(),
})
    .refine((line) => line.acceptedQuantityMilli === undefined ||
    line.rejectedQuantityMilli === undefined ||
    line.acceptedQuantityMilli + line.rejectedQuantityMilli === line.deliveredQuantityMilli, { path: ['acceptedQuantityMilli'], message: 'Aceita + recusada precisa dar o total entregue' });
const receivingHeaderSchema = z.object({
    locationId: idSchema.optional().nullable(),
    receivedAt: z.string().optional().nullable(),
    documentNumber: z.string().trim().max(40).optional().nullable(),
    documentKey: accessKeySchema.optional().nullable(),
    notes: z.string().trim().max(1000).optional().nullable(),
});
/**
 * Abre um recebimento a partir de um pedido EMITIDO com saldo. Sem `items`
 * nasce um rascunho com todas as linhas pendentes zeradas; com `items` já
 * grava a conferência; com `conclude` faz tudo de uma vez — a entrega
 * simples, sem divergência, não precisa de dois passos.
 */
export const receivingInputSchema = receivingHeaderSchema.extend({
    purchaseOrderId: idSchema,
    items: z.array(receivingLineInputSchema).optional(),
    conclude: z.boolean().optional(),
});
export const receivingUpdateSchema = receivingHeaderSchema.extend({
    items: z.array(receivingLineInputSchema).optional(),
});
export const receivingConcludeSchema = z.object({
    items: z.array(receivingLineInputSchema).optional(),
});
/** Cancelar rascunho ou ESTORNAR concluído: sempre com motivo. */
export const receivingCancelSchema = z.object({
    reason: z.string().trim().min(5, 'Diga por que está cancelando').max(500),
});
export const receivingDivergenceInputSchema = z.object({
    receivingItemId: idSchema.optional().nullable(),
    type: receivingDivergenceTypeSchema,
    quantityMilli: qtyMilliSchema.min(0).default(0),
    description: z.string().trim().min(3, 'Descreva a divergência').max(1000),
});
export const receivingDivergenceUpdateSchema = z
    .object({
    status: receivingDivergenceStatusSchema,
    resolution: z.string().trim().max(1000).optional().nullable(),
})
    .refine((v) => !['RESOLVIDA', 'ACEITA', 'RECUSADA'].includes(v.status) || (v.resolution ?? '').trim().length >= 3, { path: ['resolution'], message: 'Registre como a divergência foi tratada' });
export const receivingListQuerySchema = paginationQuerySchema.extend({
    status: receivingStatusSchema.optional(),
    situation: receivingSituationSchema.optional(),
    stockStatus: receivingStockStatusSchema.optional(),
    personId: idSchema.optional(),
    costCenterId: idSchema.optional(),
    purchaseOrderId: idSchema.optional(),
    locationId: idSchema.optional(),
    receivedById: idSchema.optional(),
    hasDivergence: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
});
export const receivingListResponseSchema = paginated(receivingSchema);
/** Cartões do topo, sempre sobre o filtro em uso. */
export const receivingSummarySchema = z.object({
    received: z.number().int(),
    inProgress: z.number().int(),
    partial: z.number().int(),
    withDivergence: z.number().int(),
    totalCents: z.number().int(),
});
/** Pedido que ainda pode receber: emitido, com saldo. */
export const receivableOrderSchema = z.object({
    id: idSchema,
    code: z.string(),
    supplierName: z.string().nullable(),
    supplierDocument: z.string().nullable(),
    costCenterName: z.string().nullable(),
    locationId: idSchema.nullable(),
    locationName: z.string().nullable(),
    expectedAt: z.string().nullable(),
    receiving: purchaseOrderReceivingSchema,
    pendingItems: z.number().int(),
    totalCents: z.number().int(),
    /// Já existe conferência aberta deste pedido — a tela leva para ela em
    /// vez de abrir outra.
    openReceivingId: idSchema.nullable(),
    openReceivingCode: z.string().nullable(),
});
export const receivableOrdersResponseSchema = z.object({ data: z.array(receivableOrderSchema) });
export const receivingTimelineResponseSchema = requisitionTimelineResponseSchema;
/* ======================== Documentos fiscais / NF-e ====================== */
/**
 * O que o papel É.
 *
 * Boleto, contrato e proposta entram na Central pela mesma porta da NF-e, mas
 * não têm chave, não têm item e não lançam estoque. Tratar tudo como "arquivo"
 * é o que faz o boleto sumir no meio das notas.
 */
export const FISCAL_DOCUMENT_KINDS = [
    'NFE',
    'NFSE',
    'NFCE',
    'CTE',
    'DANFE',
    'XML',
    'BOLETO',
    'FATURA',
    'RECIBO',
    'COMPROVANTE',
    'CONTRATO',
    'PROPOSTA',
    'OUTRO',
];
export const fiscalDocumentKindSchema = z.enum(FISCAL_DOCUMENT_KINDS);
export const FISCAL_DOCUMENT_KIND_LABELS = {
    NFE: 'NF-e',
    NFSE: 'NFS-e',
    NFCE: 'NFC-e',
    CTE: 'CT-e',
    DANFE: 'DANFE',
    XML: 'XML',
    BOLETO: 'Boleto',
    FATURA: 'Fatura',
    RECIBO: 'Recibo',
    COMPROVANTE: 'Comprovante',
    CONTRATO: 'Contrato',
    PROPOSTA: 'Proposta',
    OUTRO: 'Outro',
};
/**
 * Tipos que SÓ existem com chave de acesso e XML.
 *
 * É o que impede o cadastro manual de virar porta dos fundos: quem tenta
 * registrar "NF-e" na mão está fugindo da importação do XML.
 */
export const XML_ONLY_KINDS = ['NFE', 'NFCE', 'CTE'];
export const FISCAL_DOCUMENT_ORIGINS = ['XML', 'SEFAZ', 'MANUAL', 'INTEGRACAO', 'UPLOAD'];
export const fiscalDocumentOriginSchema = z.enum(FISCAL_DOCUMENT_ORIGINS);
export const FISCAL_DOCUMENT_ORIGIN_LABELS = {
    XML: 'XML importado',
    SEFAZ: 'Baixado da SEFAZ',
    MANUAL: 'Cadastro manual',
    INTEGRACAO: 'Integração',
    UPLOAD: 'Arquivo enviado',
};
/** Situação do documento na SEFAZ. */
export const FISCAL_STATUSES = [
    'AUTORIZADA',
    'CANCELADA',
    'DENEGADA',
    'REJEITADA',
    'DESCONHECIDA',
    'NAO_APLICA',
];
export const fiscalStatusSchema = z.enum(FISCAL_STATUSES);
/**
 * Sem autorização válida na SEFAZ.
 *
 * Cancelada, denegada e rejeitada significam a mesma coisa para quem opera:
 * a nota não vira conta a pagar nem entrada de estoque. Fica aqui, num lugar
 * só, porque a API e a tela precisam responder igual — quando a tela tinha a
 * própria lista, a nota rejeitada aparecia sem alerta e com o botão Conferir
 * ligado, que devolvia 409.
 */
export const DEAD_FISCAL_STATUSES = [
    'CANCELADA',
    'DENEGADA',
    'REJEITADA',
];
export const FISCAL_STATUS_LABELS = {
    AUTORIZADA: 'Autorizada',
    CANCELADA: 'Cancelada',
    DENEGADA: 'Denegada',
    REJEITADA: 'Rejeitada',
    DESCONHECIDA: 'Não consultada',
    NAO_APLICA: 'Não se aplica',
};
/** Situação da CONFERÊNCIA (nota × pedido × recebimento). */
export const FISCAL_CHECK_STATUSES = [
    'PENDENTE',
    'EM_CONFERENCIA',
    'VALIDADO',
    'COM_DIVERGENCIA',
    'DISPENSADA',
];
export const fiscalCheckStatusSchema = z.enum(FISCAL_CHECK_STATUSES);
export const FISCAL_CHECK_STATUS_LABELS = {
    PENDENTE: 'Conferência pendente',
    EM_CONFERENCIA: 'Em conferência',
    VALIDADO: 'Validado',
    COM_DIVERGENCIA: 'Com divergência',
    DISPENSADA: 'Dispensada',
};
/**
 * Destino do documento.
 *
 * `COBERTO` é o estoque que entrou pelo RECEBIMENTO: ninguém precisa fazer
 * nada, e não foi lançado por aqui. `BLOQUEADO` é a nota cancelada — não dá
 * para lançar, e dizer "pendente" faria alguém tentar.
 */
export const FISCAL_TARGET_STATUSES = [
    'NAO_APLICA',
    'PENDENTE',
    'LANCADO',
    'COBERTO',
    'BLOQUEADO',
];
export const fiscalTargetStatusSchema = z.enum(FISCAL_TARGET_STATUSES);
export const FISCAL_TARGET_STATUS_LABELS = {
    NAO_APLICA: 'Não se aplica',
    PENDENTE: 'Pendente',
    LANCADO: 'Lançado',
    COBERTO: 'Coberto pelo recebimento',
    BLOQUEADO: 'Bloqueado',
};
export const FISCAL_DOCUMENT_STATUSES = ['PENDENTE', 'PARCIAL', 'LANCADO', 'IGNORADO'];
export const fiscalDocumentStatusSchema = z.enum(FISCAL_DOCUMENT_STATUSES);
export const FISCAL_DOCUMENT_STATUS_LABELS = {
    PENDENTE: 'Pendente',
    PARCIAL: 'Parcial',
    LANCADO: 'Lançado',
    IGNORADO: 'Ignorado',
};
export const FISCAL_DIVERGENCE_TYPES = [
    'FORNECEDOR_DIVERGENTE',
    'ITEM_NAO_PEDIDO',
    'QUANTIDADE_MAIOR',
    'QUANTIDADE_MENOR',
    'PRECO_DIVERGENTE',
    'TOTAL_DIVERGENTE',
    'FRETE_DIVERGENTE',
    'DESCONTO_DIVERGENTE',
    'QUANTIDADE_RECEBIDA_DIVERGENTE',
    'DOCUMENTO_INVALIDO',
    'OUTRO',
];
export const fiscalDivergenceTypeSchema = z.enum(FISCAL_DIVERGENCE_TYPES);
export const FISCAL_DIVERGENCE_TYPE_LABELS = {
    FORNECEDOR_DIVERGENTE: 'Fornecedor diferente do pedido',
    ITEM_NAO_PEDIDO: 'Item que não estava no pedido',
    QUANTIDADE_MAIOR: 'Quantidade maior que a pedida',
    QUANTIDADE_MENOR: 'Quantidade menor que a pedida',
    PRECO_DIVERGENTE: 'Preço diferente do pedido',
    TOTAL_DIVERGENTE: 'Total diferente do pedido',
    FRETE_DIVERGENTE: 'Frete diferente do pedido',
    DESCONTO_DIVERGENTE: 'Desconto diferente do pedido',
    QUANTIDADE_RECEBIDA_DIVERGENTE: 'Quantidade na nota maior que a aceita no recebimento',
    DOCUMENTO_INVALIDO: 'Documento inválido',
    OUTRO: 'Outro',
};
export const FISCAL_DIVERGENCE_STATUSES = [
    'ABERTA',
    'EM_ANALISE',
    'RESOLVIDA',
    'ACEITA',
    'RECUSADA',
];
export const fiscalDivergenceStatusSchema = z.enum(FISCAL_DIVERGENCE_STATUSES);
export const FISCAL_DIVERGENCE_STATUS_LABELS = {
    ABERTA: 'Aberta',
    EM_ANALISE: 'Em análise',
    RESOLVIDA: 'Resolvida',
    ACEITA: 'Aceita',
    RECUSADA: 'Recusada',
};
/** Divergência que ainda pesa: enquanto houver uma destas, a nota não valida. */
export const OPEN_FISCAL_DIVERGENCE_STATUSES = [
    'ABERTA',
    'EM_ANALISE',
];
export const FISCAL_EVENT_KINDS = [
    'CANCELAMENTO',
    'CARTA_CORRECAO',
    'CIENCIA',
    'CONFIRMACAO',
    'DESCONHECIMENTO',
    'OPERACAO_NAO_REALIZADA',
    'OUTRO',
];
export const fiscalEventKindSchema = z.enum(FISCAL_EVENT_KINDS);
export const FISCAL_EVENT_KIND_LABELS = {
    CANCELAMENTO: 'Cancelamento',
    CARTA_CORRECAO: 'Carta de correção',
    CIENCIA: 'Ciência da operação',
    CONFIRMACAO: 'Confirmação da operação',
    DESCONHECIMENTO: 'Desconhecimento da operação',
    OPERACAO_NAO_REALIZADA: 'Operação não realizada',
    OUTRO: 'Outro evento',
};
export const fiscalDocumentItemSchema = z.object({
    id: idSchema,
    sequence: z.number().int(),
    supplierCode: z.string().nullable(),
    description: z.string(),
    ncm: z.string().nullable(),
    cfop: z.string().nullable(),
    unit: measureUnitSchema,
    quantityMilli: z.number().int(),
    unitPriceCents: z.number().int(),
    discountCents: z.number().int(),
    freightCents: z.number().int(),
    totalCents: z.number().int(),
    itemId: idSchema.nullable(),
    sku: z.string().nullable(),
    /// O de-para que o operador já fez para ESTE fornecedor com ESTE código, na
    /// última vez. Só vem no detalhe, e só para linha ainda sem item amarrado —
    /// sugestão não desfaz decisão.
    suggestedItemId: idSchema.nullable().optional(),
    suggestedItemLabel: z.string().nullable().optional(),
    /// A unidade do item sugerido. Vai junto porque a tela precisa dela para
    /// saber se pede o fator — e o item pode estar fora da primeira página da
    /// busca, onde a tela não o encontraria.
    suggestedItemUnit: measureUnitSchema.nullable().optional(),
    /// Se o item sugerido controla lote. Mesmo motivo da unidade: sem isto a
    /// tela não mostraria os campos de lote para um item fora da primeira
    /// página, e o operador levaria uma recusa da API em vez do formulário.
    suggestedItemTracksBatch: z.boolean().nullable().optional(),
    suggestedFactorMilli: z.number().int().nullable().optional(),
    suggestedUsedAt: z.string().nullable().optional(),
});
export const fiscalDivergenceSchema = z.object({
    id: idSchema,
    documentId: idSchema,
    documentItemId: idSchema.nullable(),
    itemDescription: z.string().nullable(),
    purchaseOrderId: idSchema.nullable(),
    purchaseOrderCode: z.string().nullable(),
    receivingId: idSchema.nullable(),
    receivingCode: z.string().nullable(),
    type: fiscalDivergenceTypeSchema,
    status: fiscalDivergenceStatusSchema,
    expected: z.string().nullable(),
    found: z.string().nullable(),
    description: z.string(),
    resolution: z.string().nullable(),
    automatic: z.boolean(),
    resolvedById: idSchema.nullable(),
    resolvedByName: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    createdAt: z.string(),
    createdById: idSchema.nullable(),
    createdByName: z.string().nullable(),
});
export const fiscalEventSchema = z.object({
    id: idSchema,
    kind: fiscalEventKindSchema,
    protocol: z.string().nullable(),
    occurredAt: z.string(),
    description: z.string(),
    createdAt: z.string(),
    createdById: idSchema.nullable(),
    createdByName: z.string().nullable(),
});
/** Pedido amarrado (ou sugerido) à nota. */
export const fiscalDocumentOrderSchema = z.object({
    id: idSchema,
    purchaseOrderId: idSchema,
    purchaseOrderCode: z.string(),
    supplierName: z.string().nullable(),
    costCenterName: z.string().nullable(),
    totalCents: z.number().int(),
    status: purchaseOrderStatusSchema,
    suggested: z.boolean(),
    confirmed: z.boolean(),
    createdAt: z.string(),
});
/** Recebimento amarrado à nota. */
export const fiscalDocumentReceivingSchema = z.object({
    id: idSchema,
    receivingId: idSchema,
    receivingCode: z.string(),
    purchaseOrderCode: z.string().nullable(),
    locationName: z.string().nullable(),
    receivedAt: z.string(),
    status: receivingStatusSchema,
    totalCents: z.number().int(),
    createdAt: z.string(),
});
/**
 * Situação do documento na lista — derivada, nunca gravada.
 *
 * É a pergunta que o operador faz olhando a tela: "o que falta nesse aí?".
 * Quatro colunas de status não cabem na linha da tabela; esta cabe.
 */
export const FISCAL_SITUATIONS = [
    'CANCELADA',
    'REJEITADA',
    'COM_DIVERGENCIA',
    'AGUARDANDO_CONFERENCIA',
    'AGUARDANDO_LANCAMENTO',
    'LANCADA',
    'IGNORADA',
];
export const fiscalSituationSchema = z.enum(FISCAL_SITUATIONS);
export const FISCAL_SITUATION_LABELS = {
    CANCELADA: 'Cancelada',
    REJEITADA: 'Rejeitada na SEFAZ',
    COM_DIVERGENCIA: 'Com divergência',
    AGUARDANDO_CONFERENCIA: 'Aguardando conferência',
    AGUARDANDO_LANCAMENTO: 'Aguardando lançamento',
    LANCADA: 'Lançada',
    IGNORADA: 'Ignorada',
};
export const fiscalDocumentSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    /// DOC-2026-000245. Não substitui número, série nem chave.
    code: z.string(),
    kind: fiscalDocumentKindSchema,
    origin: fiscalDocumentOriginSchema,
    accessKey: z.string().nullable(),
    number: z.string(),
    series: z.string().nullable(),
    model: z.string().nullable(),
    protocol: z.string().nullable(),
    authorizedAt: z.string().nullable(),
    personId: idSchema.nullable(),
    supplierName: z.string(),
    /// Fantasia do emitente: o `xFant` da nota, ou o do cadastro quando a nota
    /// não trouxe. Nulo quando nenhum dos dois existe.
    supplierTradeName: z.string().nullable(),
    supplierDocument: z.string().nullable(),
    supplierStateRegistration: z.string().nullable(),
    recipientName: z.string().nullable(),
    recipientDocument: z.string().nullable(),
    issueDate: z.string(),
    entryDate: z.string().nullable(),
    productsCents: z.number().int(),
    discountCents: z.number().int(),
    freightCents: z.number().int(),
    insuranceCents: z.number().int(),
    otherCents: z.number().int(),
    taxCents: z.number().int(),
    totalCents: z.number().int(),
    fiscalStatus: fiscalStatusSchema,
    checkStatus: fiscalCheckStatusSchema,
    status: fiscalDocumentStatusSchema,
    situation: fiscalSituationSchema,
    financialStatus: fiscalTargetStatusSchema,
    stockStatus: fiscalTargetStatusSchema,
    hasDivergence: z.boolean(),
    hasXml: z.boolean(),
    notes: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    ignoredAt: z.string().nullable(),
    /// Lançamento financeiro gerado por esta nota, quando existe. É o que
    /// impede a segunda conta a pagar do mesmo documento.
    financialEntryId: idSchema.nullable(),
    financialEntryCode: z.string().nullable(),
    itemCount: z.number().int(),
    items: z.array(fiscalDocumentItemSchema),
    orders: z.array(fiscalDocumentOrderSchema),
    receivings: z.array(fiscalDocumentReceivingSchema),
    divergences: z.array(fiscalDivergenceSchema),
    events: z.array(fiscalEventSchema),
});
/** Importação do XML da NF-e. O parser extrai emitente, itens e totais. */
export const importXmlSchema = z.object({
    xml: z.string().min(50, 'Cole o conteúdo do XML da NF-e'),
});
/**
 * Cadastro manual — o recibo do chaveiro, o boleto, o contrato.
 *
 * NF-e, NFC-e e CT-e ficam DE FORA: esses têm XML, e deixar cadastrar na mão
 * é abrir a porta dos fundos para escapar da conferência da chave.
 */
export const manualDocumentSchema = z.object({
    kind: fiscalDocumentKindSchema.refine((k) => !XML_ONLY_KINDS.includes(k), {
        message: 'NF-e, NFC-e e CT-e entram pelo XML — importe o arquivo do fornecedor',
    }),
    number: z.string().trim().min(1, 'Informe o número ou a referência do documento').max(60),
    series: z.string().trim().max(10).optional().nullable(),
    personId: idSchema.optional().nullable(),
    supplierName: z.string().trim().min(2, 'Informe quem emitiu').max(180),
    supplierDocument: z.string().trim().max(20).optional().nullable(),
    issueDate: isoDateSchema,
    entryDate: isoDateSchema.optional().nullable(),
    totalCents: centsSchema.min(0),
    freightCents: centsSchema.min(0).optional(),
    discountCents: centsSchema.min(0).optional(),
    /// Por que este documento está entrando na mão, e não por XML.
    reason: z.string().trim().min(5, 'Diga de onde veio este documento').max(500),
    notes: z.string().trim().max(1000).optional().nullable(),
});
/** Edição do cabeçalho. Campo fiscal de nota com XML não se altera aqui. */
export const fiscalDocumentUpdateSchema = z.object({
    personId: idSchema.optional().nullable(),
    entryDate: isoDateSchema.optional().nullable(),
    notes: z.string().trim().max(1000).optional().nullable(),
});
/**
 * "Onde este documento reflete?" — a pergunta que o fluxo faz ANTES dos campos.
 *
 * Uma NF-e de material da obra vira conta a pagar E entrada de estoque numa
 * ação só; uma NFS-e de serviço reflete só no financeiro. Nota com pedido ou
 * recebimento vinculado NÃO entra no estoque por aqui: quem dá a quantidade
 * física verdadeira é o recebimento aceito.
 */
export const postDocumentSchema = z
    .object({
    toFinancial: z.boolean().default(false),
    toStock: z.boolean().default(false),
    financial: z
        .object({
        costCenterId: idSchema,
        chartAccountId: idSchema,
        competenceDate: isoDateSchema,
        installments: z
            .array(z.object({ dueDate: isoDateSchema, amountCents: centsSchema.positive() }))
            .min(1, 'Informe ao menos uma parcela'),
    })
        .optional(),
    stock: z
        .object({
        locationId: idSchema,
        /// Amarra cada linha da nota a um item do estoque.
        links: z
            .array(z.object({
            documentItemId: idSchema,
            itemId: idSchema,
            /// Quantas unidades DO CATÁLOGO vêm em uma unidade DA NOTA, em
            /// milésimos: caixa de 12 é 12000, e 1:1 é 1000.
            ///
            /// Obrigatório quando a unidade da nota é diferente da unidade
            /// do item — inclusive para dizer que é 1:1. Sem isso, a caixa
            /// de 12 entrava como 1 unidade pelo preço da caixa: estoque a
            /// menos e custo médio 12 vezes maior, calado.
            conversionFactorMilli: z
                .number()
                .int('O fator de conversão não pode ter mais de três casas')
                .positive('O fator de conversão precisa ser maior que zero')
                .max(1_000_000_000)
                .optional(),
            /// Lote e validade, quando o item amarrado controla lote. O XML
            /// quase nunca traz: quem digita é quem está com a caixa.
            batchCode: z.string().trim().max(60).toUpperCase().optional().nullable(),
            batchExpiry: isoDateSchema.optional().nullable(),
        }))
            .min(1, 'Vincule ao menos uma linha da nota a um item'),
    })
        .optional(),
})
    .refine((v) => v.toFinancial || v.toStock, {
    message: 'Escolha ao menos um destino: Financeiro ou Estoque',
    path: ['toFinancial'],
})
    .refine((v) => !v.toFinancial || v.financial, {
    message: 'Informe os dados do lançamento financeiro',
    path: ['financial'],
})
    .refine((v) => !v.toStock || v.stock, {
    message: 'Informe o local de estoque e o vínculo dos itens',
    path: ['stock'],
});
/**
 * Conciliação financeira da nota com um lançamento que já existe.
 *
 * O caso real: a despesa foi digitada na mão quando o boleto chegou, e dias
 * depois o XML da nota entra na Central. Lançar de novo criaria uma SEGUNDA
 * conta a pagar do mesmo dinheiro — e hoje nada impede isso. Conciliar amarra
 * a nota ao lançamento que já está lá, em vez de criar outro.
 */
export const reconcileCandidateSchema = z.object({
    id: idSchema,
    code: z.string(),
    description: z.string(),
    personId: idSchema.nullable(),
    personName: z.string().nullable(),
    documentNumber: z.string().nullable(),
    issueDate: isoDateSchema,
    competenceDate: isoDateSchema,
    totalCents: centsSchema,
    /** Nota menos lançamento. Positivo: a nota é maior do que foi lançado. */
    differenceCents: z.number().int(),
    costCenterName: z.string().nullable(),
    chartAccountName: z.string().nullable(),
    /**
     * Por que este lançamento entrou na lista. O operador precisa poder
     * DISCORDAR da sugestão — uma lista sem motivo vira chute assinado por ele.
     */
    reasons: z.array(z.string()),
});
export const reconcileFinancialSchema = z.object({
    entryId: idSchema,
    /**
     * Confirmação explícita da diferença (nota − lançamento), em centavos.
     *
     * Obrigatória quando os valores não batem, e tem de ser o número EXATO que
     * a API calculou: é o que impede conciliar no susto uma nota de R$ 1.234,56
     * com uma despesa de R$ 1.200,00 e deixar R$ 34,56 fora do livro.
     */
    acceptDifferenceCents: z.number().int().optional(),
});
export const linkOrderSchema = z.object({ purchaseOrderId: idSchema });
export const linkReceivingSchema = z.object({ receivingId: idSchema });
/** Cancelamento fiscal registrado à mão, quando a SEFAZ não trouxe o evento. */
export const cancelFiscalDocumentSchema = z.object({
    reason: z.string().trim().min(5, 'Diga por que a nota está cancelada').max(500),
    protocol: z.string().trim().max(60).optional().nullable(),
    occurredAt: isoDateSchema.optional().nullable(),
});
export const fiscalDivergenceInputSchema = z.object({
    type: fiscalDivergenceTypeSchema,
    documentItemId: idSchema.optional().nullable(),
    purchaseOrderId: idSchema.optional().nullable(),
    receivingId: idSchema.optional().nullable(),
    expected: z.string().trim().max(200).optional().nullable(),
    found: z.string().trim().max(200).optional().nullable(),
    description: z.string().trim().min(5, 'Descreva a divergência').max(500),
});
export const fiscalDivergenceUpdateSchema = z
    .object({
    status: fiscalDivergenceStatusSchema,
    resolution: z.string().trim().max(500).optional().nullable(),
})
    .refine((v) => OPEN_FISCAL_DIVERGENCE_STATUSES.includes(v.status) || !!v.resolution, {
    message: 'Diga como a divergência foi resolvida',
    path: ['resolution'],
});
export const fiscalDocumentListQuerySchema = paginationQuerySchema.extend({
    status: fiscalDocumentStatusSchema.optional(),
    situation: fiscalSituationSchema.optional(),
    kind: fiscalDocumentKindSchema.optional(),
    origin: fiscalDocumentOriginSchema.optional(),
    fiscalStatus: fiscalStatusSchema.optional(),
    checkStatus: fiscalCheckStatusSchema.optional(),
    financialStatus: fiscalTargetStatusSchema.optional(),
    stockStatus: fiscalTargetStatusSchema.optional(),
    personId: idSchema.optional(),
    purchaseOrderId: idSchema.optional(),
    receivingId: idSchema.optional(),
    costCenterId: idSchema.optional(),
    unitId: idSchema.optional(),
    hasDivergence: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    minCents: centsSchema.min(0).optional(),
    maxCents: centsSchema.min(0).optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    /// Qual data o período filtra. `entrada` cai na emissão quando a nota não
    /// tem data de entrada — ela "entrou" quando foi emitida, para o filtro.
    dateField: z.enum(['entrada', 'emissao', 'cadastro']).default('emissao'),
    /// Conciliação financeira: `sim` = já virou conta a pagar; `nao` = ainda falta.
    financeiro: z.enum(['sim', 'nao']).optional(),
    /// Conciliação de estoque: `sim` = entrou no estoque (pela nota ou pelo
    /// recebimento); `nao` = ainda falta.
    estoque: z.enum(['sim', 'nao']).optional(),
});
export const fiscalDocumentListResponseSchema = paginated(fiscalDocumentSchema);
/** Cartões do topo, sempre sobre o filtro em uso. */
export const fiscalDocumentSummarySchema = z.object({
    documents: z.number().int(),
    pending: z.number().int(),
    posted: z.number().int(),
    withDivergence: z.number().int(),
    cancelled: z.number().int(),
    totalCents: z.number().int(),
    /// Notas que ainda precisam virar conta a pagar — quantas e quanto somam.
    pendingFinancialCount: z.number().int(),
    pendingFinancialCents: z.number().int(),
    /// Notas que ainda precisam entrar no estoque — quantas e quanto somam.
    pendingStockCount: z.number().int(),
    pendingStockCents: z.number().int(),
    /**
     * Quantos documentos de cada tipo, para as abas do topo da lista.
     *
     * Só vêm os tipos que EXISTEM no período: são dez tipos no sistema e, numa
     * operação de obra, oito costumam ficar em zero. Aba vazia é ruído — e, pior,
     * ensina o operador a não olhar para a fileira de abas.
     *
     * Estas contagens ignoram o filtro DE TIPO, de propósito: escolher "NF-e"
     * não pode zerar as outras abas, senão o operador perde a noção do que há
     * para ver e não consegue voltar.
     */
    byKind: z.array(z.object({ kind: fiscalDocumentKindSchema, count: z.number().int() })),
});
/**
 * Palpite de vínculo. `score` é só para ordenar a lista — quem confirma é
 * gente, e o motivo fica escrito para a pessoa poder discordar.
 */
export const suggestedOrderSchema = z.object({
    purchaseOrderId: idSchema,
    purchaseOrderCode: z.string(),
    supplierName: z.string().nullable(),
    costCenterName: z.string().nullable(),
    issuedAt: z.string().nullable(),
    totalCents: z.number().int(),
    status: purchaseOrderStatusSchema,
    score: z.number().int(),
    reasons: z.array(z.string()),
    linked: z.boolean(),
});
export const suggestedOrdersResponseSchema = z.object({ data: z.array(suggestedOrderSchema) });
/** Resultado da conferência: o que foi comparado e o que sobrou de divergência. */
export const fiscalCheckResultSchema = z.object({
    checkStatus: fiscalCheckStatusSchema,
    compared: z.number().int(),
    opened: z.number().int(),
    closed: z.number().int(),
    message: z.string(),
});
export const fiscalDocumentTimelineResponseSchema = requisitionTimelineResponseSchema;
/**
 * Rastro REQ → COT → PED → REC → NF-e.
 *
 * A pergunta que ninguém conseguia responder sem abrir cinco telas: de onde
 * veio esta nota.
 */
export const fiscalTraceSchema = z.object({
    requisitions: z.array(z.object({ id: idSchema, code: z.string(), title: z.string().nullable() })),
    quotations: z.array(z.object({ id: idSchema, code: z.string(), title: z.string().nullable() })),
    orders: z.array(z.object({ id: idSchema, code: z.string(), title: z.string().nullable() })),
    receivings: z.array(z.object({ id: idSchema, code: z.string(), title: z.string().nullable() })),
});
//# sourceMappingURL=compras.js.map